import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import type {
  SearchParams,
  SortParams,
  FetchResult,
} from "../components/Manager/types";
import { useEffect, useCallback } from "react";

export type Brand = {
  id: number;
  name: string;
  supplier_id: number;
  created_at: string;
  updated_at: string;
  // Joined data
  suppliers?: {
    id: number;
    name: string;
  } | null;
};

/**
 * Search brands by name prefix. Returns empty array for empty search string.
 * Used by autocomplete components.
 */
export async function searchBrands(
  searchText: string,
  limit = 10
): Promise<Brand[]> {
  if (!searchText.trim()) {
    return [];
  }

  const { data, error } = await supabase
    .from("brands")
    .select("*, suppliers(id, name)")
    .ilike("name", `%${searchText}%`)
    .order("name", { ascending: true })
    .limit(limit);

  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch a single brand by ID. Used to display the selected brand name.
 */
export async function fetchBrandById(id: number): Promise<Brand | null> {
  const { data, error } = await supabase
    .from("brands")
    .select("*, suppliers(id, name)")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // Not found
    throw error;
  }
  return data;
}

export async function fetchBrands(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  sortParams?: SortParams
): Promise<FetchResult<Brand>> {
  // First, get total count with search filters
  let countQuery = supabase
    .from("brands")
    .select("*", { count: "exact", head: true });

  // Apply text search filter for count (only on name)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    countQuery = countQuery.ilike("name", searchText);
  }

  // Apply supplier_id filter for count
  if (searchParams?.filters?.supplier_id) {
    countQuery = countQuery.eq(
      "supplier_id",
      searchParams.filters.supplier_id as number
    );
  }

  const { count } = await countQuery;

  // Now get the data with supplier relation
  let query = supabase.from("brands").select("*, suppliers(id, name)");

  // Apply sorting (default to name ascending if not specified)
  const sortColumn = sortParams?.column ?? "name";
  const sortAscending = sortParams?.order !== "desc";
  query = query.order(sortColumn, { ascending: sortAscending });

  // Apply text search filter (only on name)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    query = query.ilike("name", searchText);
  }

  // Apply supplier_id filter
  if (searchParams?.filters?.supplier_id) {
    query = query.eq("supplier_id", searchParams.filters.supplier_id as number);
  }

  if (limit) {
    query = query.limit(limit + 1);
  }

  if (offset) {
    query = query.range(offset, offset + (limit ?? 10));
  }

  const { error, data } = await query;

  if (error) throw error;

  const items = data ?? [];
  const hasMore = limit ? items.length > limit : false;
  const limitedData = limit ? items.slice(0, limit) : items;

  return { data: limitedData, hasMore, totalCount: count ?? undefined };
}

const brandsAtom = atom<Brand[]>([]);

export function useBrands() {
  const [brands, setBrands] = useAtom(brandsAtom);

  const fetchAllBrands = useCallback(async () => {
    try {
      const { error, data } = await supabase
        .from("brands")
        .select("*, suppliers(id, name)")
        .order("name", { ascending: true });

      if (error) throw error;
      setBrands(data ?? []);
    } catch (err) {
      console.error("Error fetching brands:", err);
    }
  }, [setBrands]);

  useEffect(() => {
    fetchAllBrands();
  }, [fetchAllBrands]);

  return [brands, fetchAllBrands] as const;
}
